/** NextGen P1 — Estimating Grid column visibility (session-local; saved views deferred). */

export type EstimatingGridColumnId =
  | 'select'
  | 'expand'
  | 'item'
  | 'description'
  | 'uom'
  | 'quantity'
  | 'rate'
  | 'amount'
  | 'links'
  | 'coverage'
  | 'cost';

export type EstimatingGridColumnDef = {
  id: EstimatingGridColumnId;
  label: string;
  /** Cannot be hidden — identity / core operational */
  locked: boolean;
  defaultVisible: boolean;
  /** CSS grid track */
  track: string;
};

export const ESTIMATING_GRID_COLUMNS: EstimatingGridColumnDef[] = [
  { id: 'select', label: 'Select', locked: true, defaultVisible: true, track: '36px' },
  { id: 'expand', label: 'Tree', locked: true, defaultVisible: true, track: '36px' },
  { id: 'item', label: 'Item', locked: true, defaultVisible: true, track: '110px' },
  { id: 'description', label: 'Description', locked: true, defaultVisible: true, track: 'minmax(180px, 1.4fr)' },
  { id: 'uom', label: 'UOM', locked: false, defaultVisible: true, track: '72px' },
  { id: 'quantity', label: 'Qty', locked: true, defaultVisible: true, track: '88px' },
  { id: 'rate', label: 'Rate', locked: false, defaultVisible: true, track: '88px' },
  { id: 'amount', label: 'Amount', locked: false, defaultVisible: true, track: '96px' },
  { id: 'links', label: 'Links', locked: false, defaultVisible: true, track: '56px' },
  { id: 'coverage', label: 'Coverage', locked: false, defaultVisible: true, track: '72px' },
  { id: 'cost', label: 'Cost', locked: false, defaultVisible: true, track: '88px' },
];

const STORAGE_KEY = 'est3-estimating-grid-columns-v1';

export function defaultVisibleColumnIds(): EstimatingGridColumnId[] {
  return ESTIMATING_GRID_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id);
}

export function lockedColumnIds(): EstimatingGridColumnId[] {
  return ESTIMATING_GRID_COLUMNS.filter((c) => c.locked).map((c) => c.id);
}

export function loadVisibleColumns(): EstimatingGridColumnId[] {
  const locked = lockedColumnIds();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultVisibleColumnIds();
    const parsed = JSON.parse(raw) as string[];
    if (!Array.isArray(parsed)) return defaultVisibleColumnIds();
    const allowed = new Set(ESTIMATING_GRID_COLUMNS.map((c) => c.id));
    const next = parsed.filter((id): id is EstimatingGridColumnId => allowed.has(id as EstimatingGridColumnId));
    for (const id of locked) {
      if (!next.includes(id)) next.unshift(id);
    }
    return next.length ? next : defaultVisibleColumnIds();
  } catch {
    return defaultVisibleColumnIds();
  }
}

export function persistVisibleColumns(ids: EstimatingGridColumnId[]) {
  try {
    const locked = lockedColumnIds();
    const merged = [...new Set([...locked, ...ids])];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* ignore */
  }
}

export function buildGridTemplate(visible: Set<EstimatingGridColumnId>): string {
  return ESTIMATING_GRID_COLUMNS.filter((c) => visible.has(c.id))
    .map((c) => c.track)
    .join(' ');
}

/** Concurrency helper — reused by bulk patch + tests. */
export function isStaleUpdatedAt(
  expectedUpdatedAt: string | null | undefined,
  actualUpdatedAt: unknown
): boolean {
  if (expectedUpdatedAt == null || !String(expectedUpdatedAt).trim()) return false;
  const expected = new Date(String(expectedUpdatedAt)).getTime();
  const actual = actualUpdatedAt ? new Date(String(actualUpdatedAt)).getTime() : NaN;
  if (!Number.isFinite(expected) || !Number.isFinite(actual)) return true;
  return expected !== actual;
}
